import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { dashboardSettingsService, trustAccountService } from '../services/apiClient';
import { notificationService } from '../services/notificationService';
import { useLocalization } from '../context/LocalizationContext';
import { useAuth } from '../context/AuthContext';
import LoadingIndicator from '../components/LoadingIndicator';
import { CreateTrustAccountRequest, TrustAccount, UpdateTrustAccountRequest } from '../types';
import { CURRENT_SYSTEM_FEE_CATEGORIES, DEFAULT_SYSTEM_CATEGORY_KEYWORDS, getSystemFeeCategoriesForAccountType, normalizeKeyword } from '../utils/systemCategoryMapping';
import { sortTrustAccountsByKind } from '../utils/trustAccount';
import './DashboardSettings.css';

type AccountKeywordType = 'Current' | 'Savings';
const KEYWORD_ACCOUNT_TYPES: AccountKeywordType[] = ['Current', 'Savings'];

const DashboardSettings: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLocalization();
  const isAdmin = user?.role === 'Admin';
  const [selectedKeywordAccountType, setSelectedKeywordAccountType] = useState<AccountKeywordType>('Current');
  const [keywordEditorsByType, setKeywordEditorsByType] = useState<Record<AccountKeywordType, Record<string, string>>>({
    Current: {},
    Savings: {},
  });
  const [keywordAddDraftsByType, setKeywordAddDraftsByType] = useState<Record<AccountKeywordType, Record<string, string>>>({
    Current: {},
    Savings: {},
  });
  const [savingAllKeywords, setSavingAllKeywords] = useState(false);
  const [persistedKeywordConfigByType, setPersistedKeywordConfigByType] = useState<Record<AccountKeywordType, Record<string, string[]>>>({
    Current: {},
    Savings: {},
  });
  const [isMockMode, setIsMockMode] = useState(false);

  const [defaultPageSize, setDefaultPageSize] = useState<number>(10);
  const [defaultShowSystemTransactions, setDefaultShowSystemTransactions] = useState<boolean>(true);
  const [savingDisplayDefaults, setSavingDisplayDefaults] = useState(false);
  const [settingsFeedback, setSettingsFeedback] = useState<{ variant: 'success' | 'danger'; message: string } | null>(null);
  const [managedTrustAccounts, setManagedTrustAccounts] = useState<TrustAccount[]>([]);
  const [loadingTrustAccounts, setLoadingTrustAccounts] = useState(false);
  const [savingTrustAccount, setSavingTrustAccount] = useState(false);
  const [trustAccountManagementFeedback, setTrustAccountManagementFeedback] = useState<{ variant: 'success' | 'danger' | 'info'; message: string } | null>(null);
  const [trustFormTouched, setTrustFormTouched] = useState(false);
  const [editingTrustAccountId, setEditingTrustAccountId] = useState<number | null>(null);
  const [editTrustFormTouched, setEditTrustFormTouched] = useState(false);
  const [editingTrustAccount, setEditingTrustAccount] = useState<UpdateTrustAccountRequest>({
    bankName: '',
    accountName: '',
    accountNumber: '',
    accountKind: 'Current',
    branchCode: '',
  });
  const [newTrustAccount, setNewTrustAccount] = useState<CreateTrustAccountRequest>({
    bankName: '',
    accountName: '',
    accountNumber: '',
    accountKind: 'Current',
    branchCode: '',
  });
  const isSettingsBusy = savingTrustAccount || savingDisplayDefaults || savingAllKeywords || loadingTrustAccounts;

  const normalizeKeywordConfig = (source?: Record<string, string[]>, useDefaults = false) => {
    return CURRENT_SYSTEM_FEE_CATEGORIES.reduce<Record<string, string[]>>((accumulator, category) => {
      const defaults = DEFAULT_SYSTEM_CATEGORY_KEYWORDS[category] || [];
      const provided = source?.[category] || (useDefaults ? defaults : []);
      accumulator[category] = Array.from(new Set((provided || []).map(normalizeKeyword).filter(Boolean)));
      if (useDefaults && accumulator[category].length === 0) {
        accumulator[category] = Array.from(new Set(defaults.map(normalizeKeyword).filter(Boolean)));
      }
      return accumulator;
    }, {});
  };

  const createEmptyKeywordConfigByType = (useDefaults = false): Record<AccountKeywordType, Record<string, string[]>> => ({
    Current: normalizeKeywordConfig(undefined, useDefaults),
    Savings: normalizeKeywordConfig(undefined, useDefaults),
  });

  const normalizeKeywordConfigByAccountType = (
    source?: Record<string, Record<string, string[]>>,
    legacy?: Record<string, string[]>,
    useDefaults = false
  ): Record<AccountKeywordType, Record<string, string[]>> => {
    const legacyNormalized = normalizeKeywordConfig(legacy, useDefaults);

    const currentSource = source?.Current || source?.current || source?.Main || source?.main;
    const savingsSource = source?.Savings || source?.savings;

    const normalizeByCategories = (categories: string[], provided?: Record<string, string[]>, fallback?: Record<string, string[]>) => {
      return categories.reduce<Record<string, string[]>>((accumulator, category) => {
        const defaults = DEFAULT_SYSTEM_CATEGORY_KEYWORDS[category] || [];
        const fromProvided = provided?.[category];
        const fromFallback = fallback?.[category];
        const rawKeywords = fromProvided || fromFallback || (useDefaults ? defaults : []);

        accumulator[category] = Array.from(new Set((rawKeywords || []).map(normalizeKeyword).filter(Boolean)));
        if (useDefaults && accumulator[category].length === 0) {
          accumulator[category] = Array.from(new Set(defaults.map(normalizeKeyword).filter(Boolean)));
        }

        return accumulator;
      }, {});
    };

    return {
      Current: normalizeByCategories(getSystemFeeCategoriesForAccountType('Current'), currentSource, legacyNormalized),
      Savings: normalizeByCategories(getSystemFeeCategoriesForAccountType('Savings'), savingsSource),
    };
  };

  const buildKeywordEditorsFromConfig = (config: Record<string, string[]>) => {
    return Object.keys(config).reduce<Record<string, string>>((accumulator, category) => {
      accumulator[category] = (config[category] || []).join(', ');
      return accumulator;
    }, {});
  };

  const keywordCategoriesForSelectedType = React.useMemo(() => {
    return getSystemFeeCategoriesForAccountType(selectedKeywordAccountType);
  }, [selectedKeywordAccountType]);

  const buildKeywordEditorsByType = (configByType: Record<AccountKeywordType, Record<string, string[]>>) => ({
    Current: buildKeywordEditorsFromConfig(configByType.Current),
    Savings: buildKeywordEditorsFromConfig(configByType.Savings),
  });

  useEffect(() => {
    const load = async () => {
      try {
        const settings = await dashboardSettingsService.get();
        const settingsMockMode = settings.isMockMode === true;
        const keywordConfigByType = normalizeKeywordConfigByAccountType(
          settings.systemCategoryKeywordsByAccountType,
          settings.systemCategoryKeywords,
          settingsMockMode
        );
        setIsMockMode(settingsMockMode);

        setDefaultPageSize(settings.defaultPageSize);
        setDefaultShowSystemTransactions(settings.defaultShowSystemTransactions);
        setPersistedKeywordConfigByType(keywordConfigByType);
        setKeywordEditorsByType(buildKeywordEditorsByType(keywordConfigByType));
        setKeywordAddDraftsByType({ Current: {}, Savings: {} });
      } catch (err) {
        console.error(err);
        setSettingsFeedback({ variant: 'danger', message: t('couldNotLoadDashboardSettingsData') });
      }
    };

    load();
    // We intentionally load once on mount; tying this to `t` causes reload/reset on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTrustAccounts = async () => {
    try {
      setLoadingTrustAccounts(true);
      const accounts = sortTrustAccountsByKind(await trustAccountService.getAll());
      setManagedTrustAccounts(accounts);
    } catch (error) {
      console.error(error);
      setTrustAccountManagementFeedback({ variant: 'danger', message: t('couldNotLoadData') });
    } finally {
      setLoadingTrustAccounts(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      setManagedTrustAccounts([]);
      return;
    }

    loadTrustAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const getAccountTypeFromKind = (accountKind?: string): 'Current' | 'Savings' => {
    return (accountKind || '').trim().toLowerCase() === 'savings' ? 'Savings' : 'Current';
  };

  const normalizeAccountNumber = (value: string) => value.replace(/\s+/g, '');

  const configuredAccountTypes = React.useMemo(() => {
    const configured = new Set<'Current' | 'Savings'>();

    managedTrustAccounts.forEach((account) => {
      configured.add(getAccountTypeFromKind(account.accountKind));
    });

    return configured;
  }, [managedTrustAccounts]);

  const availableAccountTypes = React.useMemo(() => {
    const options: Array<'Current' | 'Savings'> = [];
    if (!configuredAccountTypes.has('Current')) options.push('Current');
    if (!configuredAccountTypes.has('Savings')) options.push('Savings');
    return options;
  }, [configuredAccountTypes]);

  const duplicateAccountNameExists = React.useMemo(() => {
    const accountName = newTrustAccount.accountName.trim();
    if (!accountName) return false;

    return managedTrustAccounts.some((account) =>
      (account.accountName || '').trim().toLowerCase() === accountName.toLowerCase());
  }, [managedTrustAccounts, newTrustAccount.accountName]);

  const duplicateAccountNumberExists = React.useMemo(() => {
    const accountNumber = normalizeAccountNumber(newTrustAccount.accountNumber);
    if (!accountNumber) return false;

    return managedTrustAccounts.some((account) =>
      normalizeAccountNumber(account.accountNumber || '').toLowerCase() === accountNumber.toLowerCase());
  }, [managedTrustAccounts, newTrustAccount.accountNumber]);

  const duplicateEditAccountNameExists = React.useMemo(() => {
    if (!editingTrustAccountId) return false;

    const accountName = editingTrustAccount.accountName.trim();
    if (!accountName) return false;

    return managedTrustAccounts.some((account) =>
      account.id !== editingTrustAccountId
      && (account.accountName || '').trim().toLowerCase() === accountName.toLowerCase());
  }, [editingTrustAccount.accountName, editingTrustAccountId, managedTrustAccounts]);

  const duplicateEditAccountNumberExists = React.useMemo(() => {
    if (!editingTrustAccountId) return false;

    const accountNumber = normalizeAccountNumber(editingTrustAccount.accountNumber);
    if (!accountNumber) return false;

    return managedTrustAccounts.some((account) =>
      account.id !== editingTrustAccountId
      && normalizeAccountNumber(account.accountNumber || '').toLowerCase() === accountNumber.toLowerCase());
  }, [editingTrustAccount.accountNumber, editingTrustAccountId, managedTrustAccounts]);

  useEffect(() => {
    if (availableAccountTypes.length === 0) {
      return;
    }

    if (!availableAccountTypes.includes(newTrustAccount.accountKind)) {
      setNewTrustAccount((previous) => ({
        ...previous,
        accountKind: availableAccountTypes[0],
      }));
    }
  }, [availableAccountTypes, newTrustAccount.accountKind]);

  const startEditingTrustAccount = (account: TrustAccount) => {
    setEditingTrustAccountId(account.id);
    setEditTrustFormTouched(false);
    setEditingTrustAccount({
      bankName: account.bankName || '',
      accountName: account.accountName || '',
      accountNumber: account.accountNumber || '',
      accountKind: getAccountTypeFromKind(account.accountKind),
      branchCode: account.branchCode || '',
    });
    setTrustAccountManagementFeedback(null);
  };

  const cancelEditingTrustAccount = () => {
    setEditingTrustAccountId(null);
    setEditTrustFormTouched(false);
    setEditingTrustAccount({
      bankName: '',
      accountName: '',
      accountNumber: '',
      accountKind: 'Current',
      branchCode: '',
    });
  };

  const createTrustAccount = async () => {
    if (!isAdmin) return;

    const bankName = newTrustAccount.bankName.trim();
    const accountName = newTrustAccount.accountName.trim();
    const accountNumber = newTrustAccount.accountNumber.trim();
    const branchCode = newTrustAccount.branchCode.trim();
    const accountKind = availableAccountTypes.includes(newTrustAccount.accountKind)
      ? newTrustAccount.accountKind
      : availableAccountTypes[0];

    if (!bankName || !accountName || !accountNumber || !branchCode || !accountKind) {
      setTrustFormTouched(true);
      setTrustAccountManagementFeedback({ variant: 'danger', message: t('missingRequiredValues') });
      return;
    }

    if (duplicateAccountNameExists) {
      setTrustAccountManagementFeedback({ variant: 'danger', message: t('accountNameAlreadyExistsError') });
      return;
    }

    if (duplicateAccountNumberExists) {
      setTrustAccountManagementFeedback({ variant: 'danger', message: t('accountNumberAlreadyExistsError') });
      return;
    }

    try {
      setSavingTrustAccount(true);
      setTrustAccountManagementFeedback(null);

      await trustAccountService.create({
        bankName,
        accountName,
        accountNumber,
        accountKind,
        branchCode,
      });

      setNewTrustAccount({
        bankName: '',
        accountName: '',
        accountNumber: '',
        accountKind: availableAccountTypes.find((type) => type !== accountKind) || accountKind,
        branchCode: '',
      });
      setTrustFormTouched(false);
      await loadTrustAccounts();
      setTrustAccountManagementFeedback({ variant: 'success', message: t('trustAccountAddedSuccessfully') });
      notificationService.success(t('trustAccountAddedSuccessfully'));
    } catch (error) {
      console.error(error);
      const apiMessage = (error as any)?.response?.data?.message;
      const message = typeof apiMessage === 'string' && apiMessage.trim()
        ? apiMessage
        : t('couldNotAddTrustAccount');

      setTrustAccountManagementFeedback({ variant: 'danger', message });
      notificationService.error(message);
    } finally {
      setSavingTrustAccount(false);
    }
  };

  const updateTrustAccount = async () => {
    if (!isAdmin || !editingTrustAccountId) return;

    const bankName = editingTrustAccount.bankName.trim();
    const accountName = editingTrustAccount.accountName.trim();
    const accountNumber = editingTrustAccount.accountNumber.trim();
    const branchCode = editingTrustAccount.branchCode.trim();

    if (!bankName || !accountName || !accountNumber || !branchCode) {
      setEditTrustFormTouched(true);
      setTrustAccountManagementFeedback({ variant: 'danger', message: t('missingRequiredValues') });
      return;
    }

    if (duplicateEditAccountNameExists) {
      setTrustAccountManagementFeedback({ variant: 'danger', message: t('accountNameAlreadyExistsError') });
      return;
    }

    if (duplicateEditAccountNumberExists) {
      setTrustAccountManagementFeedback({ variant: 'danger', message: t('accountNumberAlreadyExistsError') });
      return;
    }

    try {
      setSavingTrustAccount(true);
      setTrustAccountManagementFeedback(null);

      await trustAccountService.update(editingTrustAccountId, {
        bankName,
        accountName,
        accountNumber,
        accountKind: editingTrustAccount.accountKind,
        branchCode,
      });

      await loadTrustAccounts();
      cancelEditingTrustAccount();
      setTrustAccountManagementFeedback({ variant: 'success', message: t('trustAccountUpdatedSuccessfully') });
      notificationService.success(t('trustAccountUpdatedSuccessfully'));
    } catch (error) {
      console.error(error);
      const apiMessage = (error as any)?.response?.data?.message;
      const message = typeof apiMessage === 'string' && apiMessage.trim()
        ? apiMessage
        : t('couldNotUpdateTrustAccount');

      setTrustAccountManagementFeedback({ variant: 'danger', message });
      notificationService.error(message);
    } finally {
      setSavingTrustAccount(false);
    }
  };

  const saveDashboardDefaults = async () => {
    try {
      setSavingDisplayDefaults(true);
      const saved = await dashboardSettingsService.update({
        defaultPageSize,
        defaultShowSystemTransactions,
        systemCategoryKeywords: persistedKeywordConfigByType.Current,
        systemCategoryKeywordsByAccountType: persistedKeywordConfigByType,
      });

      const normalizedSavedByType = normalizeKeywordConfigByAccountType(
        saved.systemCategoryKeywordsByAccountType,
        saved.systemCategoryKeywords,
        isMockMode
      );

      setDefaultPageSize(saved.defaultPageSize);
      setDefaultShowSystemTransactions(saved.defaultShowSystemTransactions);
      setPersistedKeywordConfigByType(normalizedSavedByType);
      setSettingsFeedback({ variant: 'success', message: t('dashboardDefaultsSavedSuccessfully') });
    } catch (err) {
      console.error(err);
      setSettingsFeedback({ variant: 'danger', message: t('couldNotSaveAllKeywordChanges') });
    } finally {
      setSavingDisplayDefaults(false);
    }
  };

  const getEditorKeywordsForType = (accountType: AccountKeywordType, categoryName: string) => {
    const rawInput = keywordEditorsByType[accountType]?.[categoryName] || '';
    return Array.from(new Set(rawInput.split(',').map(normalizeKeyword).filter(Boolean)));
  };

  const parseEditorKeywords = (categoryName: string) => {
    return getEditorKeywordsForType(selectedKeywordAccountType, categoryName);
  };

  const getPersistedKeywords = (categoryName: string) =>
    Array.from(new Set((persistedKeywordConfigByType[selectedKeywordAccountType]?.[categoryName] || []).map(normalizeKeyword).filter(Boolean)));

  const hasCategoryChanges = (categoryName: string) => {
    const edited = parseEditorKeywords(categoryName);
    const persisted = getPersistedKeywords(categoryName);

    if (edited.length !== persisted.length) return true;
    return edited.some((keyword) => !persisted.includes(keyword));
  };

  const hasAnyKeywordChanges = keywordCategoriesForSelectedType.some((category) => hasCategoryChanges(category));
  const changedCategoryCount = keywordCategoriesForSelectedType.filter((category) => hasCategoryChanges(category)).length;

  const getChangedCategoryCountForType = (accountType: AccountKeywordType) => {
    return getSystemFeeCategoriesForAccountType(accountType).filter((category) => {
      const edited = getEditorKeywordsForType(accountType, category);
      const persisted = Array.from(new Set((persistedKeywordConfigByType[accountType]?.[category] || []).map(normalizeKeyword).filter(Boolean)));

      if (edited.length !== persisted.length) return true;
      return edited.some((keyword) => !persisted.includes(keyword));
    }).length;
  };

  const changedCategoryCountByType: Record<AccountKeywordType, number> = {
    Current: getChangedCategoryCountForType('Current'),
    Savings: getChangedCategoryCountForType('Savings'),
  };

  const activeKeywordAddDrafts = keywordAddDraftsByType[selectedKeywordAccountType] || {};
  const activePersistedKeywordConfig = persistedKeywordConfigByType[selectedKeywordAccountType] || normalizeKeywordConfig(undefined, isMockMode);

  const setCategoryKeywords = (categoryName: string, keywords: string[]) => {
    setKeywordEditorsByType((previous) => ({
      ...previous,
      [selectedKeywordAccountType]: {
        ...(previous[selectedKeywordAccountType] || {}),
        [categoryName]: keywords.join(', '),
      },
    }));
  };

  const addKeywordToCategory = (categoryName: string) => {
    const rawDraft = activeKeywordAddDrafts[categoryName] || '';
    const nextFromDraft = rawDraft
      .split(',')
      .map(normalizeKeyword)
      .filter(Boolean);

    if (nextFromDraft.length === 0) return;

    const existing = parseEditorKeywords(categoryName);
    const nextKeywords = Array.from(new Set([...existing, ...nextFromDraft]));
    setCategoryKeywords(categoryName, nextKeywords);

    setKeywordAddDraftsByType((previous) => ({
      ...previous,
      [selectedKeywordAccountType]: {
        ...(previous[selectedKeywordAccountType] || {}),
        [categoryName]: '',
      },
    }));
  };

  const removeKeywordFromCategory = (categoryName: string, keywordToRemove: string) => {
    const next = parseEditorKeywords(categoryName).filter((keyword) => keyword !== keywordToRemove);
    setCategoryKeywords(categoryName, next);
  };

  const saveAllKeywordChanges = async () => {
    const changedCategories = keywordCategoriesForSelectedType.filter((category) => hasCategoryChanges(category));
    if (changedCategories.length === 0) {
      notificationService.info(t('noKeywordChangesToSave'));
      return;
    }

    try {
      setSavingAllKeywords(true);
      const nextKeywordConfigByType = KEYWORD_ACCOUNT_TYPES.reduce<Record<AccountKeywordType, Record<string, string[]>>>((accountTypeAccumulator, accountType) => {
        accountTypeAccumulator[accountType] = getSystemFeeCategoriesForAccountType(accountType).reduce<Record<string, string[]>>((categoryAccumulator, category) => {
          categoryAccumulator[category] = getEditorKeywordsForType(accountType, category);
          return categoryAccumulator;
        }, {});

        return accountTypeAccumulator;
      }, createEmptyKeywordConfigByType(isMockMode));

      const saved = await dashboardSettingsService.update({
        defaultPageSize,
        defaultShowSystemTransactions,
        systemCategoryKeywords: nextKeywordConfigByType.Current,
        systemCategoryKeywordsByAccountType: nextKeywordConfigByType,
      });

      const normalizedSavedByType = normalizeKeywordConfigByAccountType(
        saved.systemCategoryKeywordsByAccountType,
        saved.systemCategoryKeywords,
        isMockMode
      );
      setPersistedKeywordConfigByType(normalizedSavedByType);
      setKeywordEditorsByType(buildKeywordEditorsByType(normalizedSavedByType));
      setKeywordAddDraftsByType({ Current: {}, Savings: {} });
      notificationService.success(t('savedAllKeywordChanges'));
    } catch (err) {
      console.error(err);
      notificationService.error(t('couldNotSaveAllKeywordChanges'));
    } finally {
      setSavingAllKeywords(false);
    }
  };

  return (
    <Container className="py-5 admin-settings-page" aria-busy={isSettingsBusy}>
      <div className="settings-hero page-header-panel">
        <div className="settings-header">
          <h1 className="mb-0 page-header-title">
            <span className="page-header-title-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                <circle cx="12" cy="12" r="3.1" fill="none" stroke="currentColor" strokeWidth="1.9" />
                <path d="M12 4.2v2.1M12 17.7v2.1M19.8 12h-2.1M6.3 12H4.2M17.5 6.5L16 8M8 16l-1.5 1.5M17.5 17.5 16 16M8 8 6.5 6.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
              </svg>
            </span>
            {t('dashboardSettingsTitle')}
          </h1>
        </div>
      </div>

        <div className="settings-tab-panel mb-4">
          <div className="d-flex justify-content-between align-items-center mb-4 settings-section-header">
            <div className="d-flex align-items-center gap-2">
              <h5 className="mb-0 settings-section-title">{t('trustAccountManagement')}</h5>
              <span className="settings-section-count">{managedTrustAccounts.length}</span>
            </div>
            {isAdmin && (
              <Button
                variant="primary"
                className="settings-primary-action d-inline-flex align-items-center gap-2"
                onClick={createTrustAccount}
                disabled={
                  savingTrustAccount
                  || availableAccountTypes.length === 0
                  || duplicateAccountNameExists
                  || duplicateAccountNumberExists
                  || !newTrustAccount.bankName.trim()
                  || !newTrustAccount.accountName.trim()
                  || !newTrustAccount.accountNumber.trim()
                  || !newTrustAccount.branchCode.trim()
                }
              >
                  {savingTrustAccount && <LoadingIndicator label={t('savingAction')} inline compact showLines={false} />}
                {savingTrustAccount ? t('savingAction') : t('addTrustAccount')}
              </Button>
            )}
          </div>

          {!isAdmin ? (
            <Alert variant="info" className="mb-0">{t('trustAccountManagementAdminOnly')}</Alert>
          ) : (
            <>
              {trustAccountManagementFeedback && (
                <Alert variant={trustAccountManagementFeedback.variant} className="mb-3">
                  {trustAccountManagementFeedback.message}
                </Alert>
              )}

              <Row className="g-3 align-items-end mb-3">
                <Col md={4}>
                  <Form.Label>{t('trustBankNameInputLabel')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={newTrustAccount.bankName}
                    onChange={(e) => setNewTrustAccount((previous) => ({ ...previous, bankName: e.target.value }))}
                    isInvalid={trustFormTouched && !newTrustAccount.bankName.trim()}
                    disabled={savingTrustAccount || availableAccountTypes.length === 0}
                  />
                  <Form.Control.Feedback type="invalid">
                    {t('missingRequiredValues')}
                  </Form.Control.Feedback>
                </Col>
                <Col md={4}>
                  <Form.Label>{t('trustAccountNameLabel')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={newTrustAccount.accountName}
                    onChange={(e) => setNewTrustAccount((previous) => ({ ...previous, accountName: e.target.value }))}
                    isInvalid={duplicateAccountNameExists || (trustFormTouched && !newTrustAccount.accountName.trim())}
                    disabled={savingTrustAccount || availableAccountTypes.length === 0}
                  />
                  <Form.Control.Feedback type="invalid">
                    {duplicateAccountNameExists ? t('accountNameAlreadyExistsError') : t('missingRequiredValues')}
                  </Form.Control.Feedback>
                </Col>
                <Col md={4}>
                  <Form.Label>{t('trustAccountNumberInputLabel')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={newTrustAccount.accountNumber}
                    onChange={(e) => setNewTrustAccount((previous) => ({ ...previous, accountNumber: e.target.value }))}
                    isInvalid={duplicateAccountNumberExists || (trustFormTouched && !newTrustAccount.accountNumber.trim())}
                    disabled={savingTrustAccount || availableAccountTypes.length === 0}
                  />
                  <Form.Control.Feedback type="invalid">
                    {duplicateAccountNumberExists ? t('accountNumberAlreadyExistsError') : t('missingRequiredValues')}
                  </Form.Control.Feedback>
                </Col>
                <Col md={2}>
                  <Form.Label>{t('accountType')}</Form.Label>
                  <Form.Select
                    value={newTrustAccount.accountKind}
                    onChange={(e) => setNewTrustAccount((previous) => ({
                      ...previous,
                      accountKind: e.target.value as 'Current' | 'Savings',
                    }))}
                    disabled={savingTrustAccount || availableAccountTypes.length === 0}
                  >
                    {availableAccountTypes.includes('Current') && (
                      <option value="Current">{t('currentAccountType')}</option>
                    )}
                    {availableAccountTypes.includes('Savings') && (
                      <option value="Savings">{t('savingsAccountType')}</option>
                    )}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label>{t('trustBranchCodeInputLabel')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={newTrustAccount.branchCode}
                    onChange={(e) => setNewTrustAccount((previous) => ({ ...previous, branchCode: e.target.value }))}
                    isInvalid={trustFormTouched && !newTrustAccount.branchCode.trim()}
                    disabled={savingTrustAccount || availableAccountTypes.length === 0}
                  />
                  <Form.Control.Feedback type="invalid">
                    {t('missingRequiredValues')}
                  </Form.Control.Feedback>
                </Col>
              </Row>

              <small className="text-muted d-block mb-3">{t('oneAccountPerTypeAllowed')}</small>
              {availableAccountTypes.length === 0 && (
                <Alert variant="info" className="mb-3">{t('allTrustAccountTypesConfigured')}</Alert>
              )}

              <div className="settings-list-label mb-2 mt-3">{t('existingTrustAccounts')}</div>
              <div className="d-flex flex-column gap-2 mt-2">
                {loadingTrustAccounts ? (
                  <LoadingIndicator label={t('loading')} compact showLines={false} subtle />
                ) : managedTrustAccounts.length === 0 ? (
                  <small className="text-muted">{t('noTrustAccountsConfigured')}</small>
                ) : managedTrustAccounts.map((account) => (
                  <div key={account.id} className="admin-trust-account-item d-flex justify-content-between align-items-center">
                    {editingTrustAccountId === account.id ? (
                      <div className="w-100">
                        <Row className="g-2 align-items-end">
                          <Col md={3}>
                            <Form.Label>{t('trustBankNameInputLabel')}</Form.Label>
                            <Form.Control
                              type="text"
                              value={editingTrustAccount.bankName}
                              onChange={(e) => setEditingTrustAccount((previous) => ({ ...previous, bankName: e.target.value }))}
                              isInvalid={editTrustFormTouched && !editingTrustAccount.bankName.trim()}
                              disabled={savingTrustAccount}
                            />
                          </Col>
                          <Col md={3}>
                            <Form.Label>{t('trustAccountNameLabel')}</Form.Label>
                            <Form.Control
                              type="text"
                              value={editingTrustAccount.accountName}
                              onChange={(e) => setEditingTrustAccount((previous) => ({ ...previous, accountName: e.target.value }))}
                              isInvalid={duplicateEditAccountNameExists || (editTrustFormTouched && !editingTrustAccount.accountName.trim())}
                              disabled={savingTrustAccount}
                            />
                          </Col>
                          <Col md={2}>
                            <Form.Label>{t('trustAccountNumberInputLabel')}</Form.Label>
                            <Form.Control
                              type="text"
                              value={editingTrustAccount.accountNumber}
                              onChange={(e) => setEditingTrustAccount((previous) => ({ ...previous, accountNumber: e.target.value }))}
                              isInvalid={duplicateEditAccountNumberExists || (editTrustFormTouched && !editingTrustAccount.accountNumber.trim())}
                              disabled={savingTrustAccount}
                            />
                          </Col>
                          <Col md={2}>
                            <Form.Label>{t('accountType')}</Form.Label>
                            <Form.Select
                              value={editingTrustAccount.accountKind}
                              onChange={(e) => setEditingTrustAccount((previous) => ({
                                ...previous,
                                accountKind: e.target.value as 'Current' | 'Savings',
                              }))}
                              disabled={savingTrustAccount}
                            >
                              <option value="Current">{t('currentAccountType')}</option>
                              <option value="Savings">{t('savingsAccountType')}</option>
                            </Form.Select>
                          </Col>
                          <Col md={2}>
                            <Form.Label>{t('trustBranchCodeInputLabel')}</Form.Label>
                            <Form.Control
                              type="text"
                              value={editingTrustAccount.branchCode}
                              onChange={(e) => setEditingTrustAccount((previous) => ({ ...previous, branchCode: e.target.value }))}
                              isInvalid={editTrustFormTouched && !editingTrustAccount.branchCode.trim()}
                              disabled={savingTrustAccount}
                            />
                          </Col>
                        </Row>
                        <div className="d-flex justify-content-end gap-2 mt-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={cancelEditingTrustAccount}
                            disabled={savingTrustAccount}
                          >
                            {t('cancel')}
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={updateTrustAccount}
                            disabled={savingTrustAccount}
                          >
                            {savingTrustAccount ? t('savingAction') : t('update')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="admin-trust-account-name">{account.accountName}</div>
                          <div className="admin-trust-account-number">{account.accountNumber}</div>
                          <small className="text-muted">{account.bankName || '-'}</small>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span className={`admin-trust-account-badge ${getAccountTypeFromKind(account.accountKind) === 'Savings' ? 'is-savings' : 'is-current'}`}>
                            {getAccountTypeFromKind(account.accountKind) === 'Savings' ? t('savingsAccountType') : t('currentAccountType')}
                          </span>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => startEditingTrustAccount(account)}
                            disabled={savingTrustAccount}
                          >
                            {t('edit')}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="settings-tab-panel mb-4">
          <div className="d-flex justify-content-between align-items-center mb-4 settings-section-header">
            <h5 className="mb-0 settings-section-title">{t('displayDefaults')}</h5>
            <Button variant="primary" className="settings-primary-action d-inline-flex align-items-center gap-2" onClick={saveDashboardDefaults} disabled={savingDisplayDefaults}>
              {savingDisplayDefaults && <LoadingIndicator label={t('savingAction')} inline compact showLines={false} />}
              {savingDisplayDefaults ? t('savingAction') : t('saveDisplayDefaults')}
            </Button>
          </div>
          {settingsFeedback && <Alert variant={settingsFeedback.variant}>{settingsFeedback.message}</Alert>}

          <Row className="g-3 align-items-center">
            <Col md={4}>
              <Form.Label>{t('defaultRowsPerPage')}</Form.Label>
              <Form.Select value={defaultPageSize} onChange={(e) => setDefaultPageSize(parseInt(e.target.value, 10))}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Form.Select>
            </Col>
            <Col md={8}>
              <Form.Check
                type="switch"
                id="default-show-system-transactions"
                label={t('showSystemFeesChargesByDefault')}
                checked={defaultShowSystemTransactions}
                onChange={(e) => setDefaultShowSystemTransactions(e.target.checked)}
              />
            </Col>
          </Row>
        </div>

        <div className={`settings-tab-panel mb-4 ${hasAnyKeywordChanges ? 'dashboard-keyword-section-unsaved' : ''}`}>
          <div className="d-flex justify-content-between align-items-center mb-4 settings-section-header">
            <div className="d-flex align-items-center gap-2">
              <h5 className="mb-0 settings-section-title">{t('transactionAutomapperKeywords')}</h5>
              {changedCategoryCount > 0 && (
                <span className="settings-section-count">{changedCategoryCount}</span>
              )}
            </div>
            <Button
              variant="primary"
              className="settings-primary-action d-inline-flex align-items-center gap-2"
              onClick={saveAllKeywordChanges}
              disabled={!hasAnyKeywordChanges || savingAllKeywords}
            >
              {savingAllKeywords && <LoadingIndicator label={t('savingAction')} inline compact showLines={false} />}
              {savingAllKeywords ? t('savingAllKeywordChanges') : t('saveAllKeywordChanges')}
            </Button>
          </div>

          <p className="settings-section-helper">
            {t('transactionAutomapperKeywordsHelper')}
          </p>
          {selectedKeywordAccountType === 'Savings' && (
            <Alert variant="info" className="mb-3">
              {t('savingsKeywordsLimitedCategories')}
            </Alert>
          )}

          <Row className="g-3 align-items-end mb-3">
            <Col md={4}>
              <Form.Label>{t('accountType')}</Form.Label>
              <Form.Select
                value={selectedKeywordAccountType}
                onChange={(event) => setSelectedKeywordAccountType(event.target.value as AccountKeywordType)}
              >
                <option value="Current">{t('currentAccountType')}</option>
                <option value="Savings">{t('savingsAccountType')}</option>
              </Form.Select>
            </Col>
            <Col md={8}>
              <div className="d-flex flex-wrap gap-2 keyword-type-status-row">
                <span className={`keyword-type-status-badge ${changedCategoryCountByType.Current > 0 ? 'is-pending' : 'is-saved'}`}>
                  {t('currentAccountType')}: {changedCategoryCountByType.Current} {t(changedCategoryCountByType.Current === 1 ? 'categoryPending' : 'categoriesPending')}
                </span>
                <span className={`keyword-type-status-badge ${changedCategoryCountByType.Savings > 0 ? 'is-pending' : 'is-saved'}`}>
                  {t('savingsAccountType')}: {changedCategoryCountByType.Savings} {t(changedCategoryCountByType.Savings === 1 ? 'categoryPending' : 'categoriesPending')}
                </span>
              </div>
            </Col>
          </Row>

          <div className="dashboard-keyword-toolbar mb-3">
            <div className="dashboard-keyword-toolbar-summary">
              <span className="dashboard-keyword-toolbar-label">{t('keywordChanges')}</span>
              <strong>{changedCategoryCount} {t(changedCategoryCount === 1 ? 'categoryPending' : 'categoriesPending')}</strong>
              <span className="ms-2 badge bg-info-subtle text-dark border">
                {t('accountType')}: {selectedKeywordAccountType === 'Savings' ? t('savingsAccountType') : t('currentAccountType')}
              </span>
            </div>
            {hasAnyKeywordChanges ? (
              <span className="dashboard-keyword-unsaved-tag">{t('unsavedChanges')}</span>
            ) : (
              <span className="dashboard-keyword-saved-tag">{t('allChangesSaved')}</span>
            )}
          </div>

          <div className="d-flex flex-column gap-3">
                  {keywordCategoriesForSelectedType.map((categoryName) => (
                    <div key={categoryName} className="dashboard-keyword-item">
                      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                        <div className="fw-500 dashboard-keyword-title">{categoryName}</div>
                        {hasCategoryChanges(categoryName) && <span className="dashboard-keyword-unsaved-tag">{t('unsaved')}</span>}
                      </div>
                      <small className="text-muted d-block mb-2 dashboard-keyword-current">
                        {t('addOrRemoveKeywordsHint')}
                      </small>

                      <div className="dashboard-keyword-chip-list mb-2">
                        {parseEditorKeywords(categoryName).length === 0 ? (
                          <span className="text-muted small">{t('noKeywordsConfigured')}</span>
                        ) : (
                          parseEditorKeywords(categoryName).map((keyword) => (
                            <button
                              type="button"
                              key={`${categoryName}-${keyword}`}
                              className="dashboard-keyword-chip"
                              onClick={(e) => e.preventDefault()}
                              disabled={savingAllKeywords}
                              title={keyword}
                            >
                              <span>{keyword}</span>
                              <span
                                role="button"
                                tabIndex={0}
                                className="dashboard-keyword-chip-remove"
                                onClick={() => removeKeywordFromCategory(categoryName, keyword)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    removeKeywordFromCategory(categoryName, keyword);
                                  }
                                }}
                              >
                                ×
                              </span>
                            </button>
                          ))
                        )}
                      </div>

                      <div className="d-flex gap-2 dashboard-keyword-input-row mb-2">
                        <Form.Control
                          type="text"
                          value={activeKeywordAddDrafts[categoryName] || ''}
                          placeholder={t('enterKeywordsCommaSeparated')}
                          onChange={(e) => {
                            const value = e.target.value;
                            setKeywordAddDraftsByType((previous) => ({
                              ...previous,
                              [selectedKeywordAccountType]: {
                                ...(previous[selectedKeywordAccountType] || {}),
                                [categoryName]: value,
                              },
                            }));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addKeywordToCategory(categoryName);
                            }
                          }}
                        />
                        <Button
                          variant="outline-primary"
                          onClick={() => addKeywordToCategory(categoryName)}
                          disabled={savingAllKeywords}
                        >
                          {t('add')}
                        </Button>
                      </div>

                      <div className="d-flex gap-2 dashboard-keyword-input-row">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="dashboard-wire-action-btn dashboard-recent-utility-btn dashboard-recent-reset-btn"
                          onClick={() => {
                            setCategoryKeywords(categoryName, activePersistedKeywordConfig[categoryName] || []);
                            setKeywordAddDraftsByType((previous) => ({
                              ...previous,
                              [selectedKeywordAccountType]: {
                                ...(previous[selectedKeywordAccountType] || {}),
                                [categoryName]: '',
                              },
                            }));
                          }}
                          disabled={savingAllKeywords || !hasCategoryChanges(categoryName)}
                        >
                          {t('reset')}
                        </Button>
                      </div>
                      <small className="text-muted d-block mt-2">
                        {t('activeKeywords')}: {(activePersistedKeywordConfig[categoryName] || []).join(', ') || t('none')}
                      </small>
                    </div>
                  ))}
          </div>
        </div>

    </Container>
  );
};

export default DashboardSettings;
